/**
 * @module engine3d/models/storage
 * @summary 3D models for the Storage & Silos family — silos, hoppers, tanks,
 *   bulk bags, IBCs and mixers.
 * @description
 *   Vertical vessels are built as surfaces of revolution from a single profile,
 *   so a silo's cone, knuckle, barrel and roof are one smooth, correctly-shaded
 *   shell rather than a stack of separate cylinders with visible seams.
 */
import {
  boxCentered,
  cylinder,
  cylinderX,
  cylinderZ,
  dome,
  flange,
  frustum,
  gearbox,
  ladder,
  merge,
  motorUnit,
  nozzle,
  p,
  pm,
  plinth,
  platform,
  rectFoot,
  revolve,
  ringLegs,
  rotateZ,
  roundFoot,
  skid,
  translate,
  v3,
  box,
  extrude,
  ngon,
  type Builder,
  type Mesh,
  type Model3,
  type Vec2,
} from './kit';

/** A vessel shell lying along +X: cylindrical barrel with dished ends. */
function horizontalShell(R: number, bodyLen: number, dishD: number, seg = 24): Mesh {
  const half = bodyLen / 2;
  const prof: Vec2[] = [];
  for (let i = 0; i <= 4; i++) {
    const t = (i / 4) * (Math.PI / 2);
    prof.push({ x: R * Math.sin(t), y: -half - dishD * Math.cos(t) });
  }
  prof.push({ x: R, y: half });
  for (let i = 1; i <= 4; i++) {
    const t = (i / 4) * (Math.PI / 2);
    prof.push({ x: R * Math.cos(t), y: half + dishD * Math.sin(t) });
  }
  return rotateZ(revolve(prof, seg, false), -Math.PI / 2);
}

/**
 * The shared vertical-vessel body: conical outlet, barrel, and a top that is
 * either a cone (silo roof) or a dish (pressure vessel).
 */
function vesselProfile(
  R: number,
  yBase: number,
  yTop: number,
  coneH: number,
  outletR: number,
  top: 'cone' | 'dish' | 'flat',
  roofH: number,
): Vec2[] {
  const prof: Vec2[] = [
    { x: outletR, y: yBase },
    { x: R * 0.99, y: yBase + coneH },
    { x: R, y: yBase + coneH + Math.min(4, coneH * 0.2) },
  ];
  const shoulder = yTop - roofH;
  prof.push({ x: R, y: shoulder });
  if (top === 'cone') {
    prof.push({ x: R * 1.04, y: shoulder + roofH * 0.06 });
    prof.push({ x: R * 0.14, y: yTop });
  } else if (top === 'dish') {
    for (let i = 1; i <= 4; i++) {
      const t = (i / 4) * (Math.PI / 2);
      prof.push({ x: R * Math.cos(t), y: shoulder + roofH * Math.sin(t) });
    }
  } else {
    prof.push({ x: R, y: yTop });
    prof.push({ x: 0, y: yTop });
  }
  return prof;
}

/** Silo — legged, cone-bottomed, cone-roofed, with a caged ladder. */
export const silo: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(w, d) / 2;
  const legH = h * 0.2;
  const coneH = h * 0.24;
  const roofH = h * 0.12;
  const outletR = Math.max(1.8, R * 0.14);
  const shell = revolve(vesselProfile(R, legH, h, coneH, outletR, 'cone', roofH), 26, false);
  return {
    parts: [
      p(plinth(w * 1.25, d * 1.25, 2), 'concrete'),
      p(ringLegs(R * 0.86, legH, 4, Math.max(1.8, R * 0.09)), 'galv'),
      p(shell, 'steel'),
      pm(
        'galv',
        translate(platform(R, h - roofH - 3, R * 0.28), 0, 0, 0),
        translate(ladder(h - roofH - 3), R + 3, 0, 0),
      ),
      pm(
        'stainless',
        // Discharge spool + butterfly valve under the cone.
        translate(cylinder(outletR * 1.5, legH * 0.45, { seg: 12 }), 0, legH * 0.3, 0),
        translate(flange(outletR * 2.1, 1.4, 12), 0, legH * 0.72, 0),
        // Roof fill nozzle.
        nozzle(v3(0, h - 1, 0), v3(0, 1, 0), R * 0.16, 5),
      ),
      p(translate(cylinder(R * 0.2, 4, { seg: 10 }), R * 0.5, h - roofH * 0.5, 0), 'safety'),
    ],
    height: h + 5,
    ports: {
      N: { p: v3(0, h + 4, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, legH * 0.3, 0), n: v3(0, -1, 0) },
      E: { p: v3(R, h * 0.62, 0), n: v3(1, 0, 0) },
      W: { p: v3(-R, h * 0.62, 0), n: v3(-1, 0, 0) },
    },
    footprint: roundFoot(R * 1.1, 12),
  };
};

/** Day bin / hopper — a square-to-round transition on a light stand. */
export const hopper: Builder = ({ w, h, d }): Model3 => {
  const legH = h * 0.24;
  const coneH = h * 0.44;
  const bodyH = h - legH - coneH;
  const R = Math.min(w, d) / 2;
  return {
    parts: [
      p(ringLegs(R * 0.82, legH, 4, 1.8), 'galv'),
      pm(
        'stainless',
        translate(frustum(R * 0.16, R, coneH, { seg: 22, capTop: false, capBottom: false }), 0, legH, 0),
        translate(cylinder(R, bodyH, { seg: 22, capBottom: false }), 0, legH + coneH, 0),
        translate(flange(R * 1.08, 1.4, 22), 0, legH + coneH + bodyH - 1.4, 0),
        translate(cylinder(R * 0.22, legH * 0.5, { seg: 10 }), 0, legH * 0.4, 0),
      ),
      // Sight glass down one side.
      p(translate(box(R * 0.3, bodyH * 0.6, 0.8), 0, legH + coneH + bodyH * 0.2, R * 0.99), 'glass'),
    ],
    height: h,
    ports: {
      N: { p: v3(0, h, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, legH * 0.4, 0), n: v3(0, -1, 0) },
      E: { p: v3(R, legH + coneH + bodyH * 0.5, 0), n: v3(1, 0, 0) },
      W: { p: v3(-R, legH + coneH + bodyH * 0.5, 0), n: v3(-1, 0, 0) },
    },
    footprint: roundFoot(R * 1.05, 12),
  };
};

/** Horizontal tank — dished ends, saddle supports, top manway. */
export const htank: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(h, d) / 2;
  const saddleH = R * 0.42;
  const bodyLen = w * 0.8;
  const cy = saddleH + R;
  const shell = horizontalShell(R, bodyLen, R * 0.45);
  const saddle = (x: number) =>
    merge(
      translate(boxCentered(R * 0.5, saddleH, R * 2.05), x, saddleH / 2, 0),
      translate(boxCentered(R * 0.7, 2, R * 2.3), x, 1, 0),
    );
  return {
    parts: [
      p(merge(saddle(bodyLen * 0.32), saddle(-bodyLen * 0.32)), 'galv'),
      p(translate(shell, 0, cy, 0), 'stainless'),
      pm(
        'stainless',
        // Top manway + side nozzles.
        translate(cylinder(R * 0.32, R * 0.3, { seg: 14 }), 0, cy + R * 0.94, 0),
        translate(flange(R * 0.42, 1.6, 14), 0, cy + R * 1.2, 0),
        nozzle(v3(0, cy + R * 0.2, R * 0.95), v3(0, 0, 1), R * 0.16, 4),
      ),
      p(translate(cylinder(R * 0.14, R * 0.5, { seg: 8 }), -bodyLen * 0.12, cy - R * 1.3, 0), 'pipe'),
    ],
    height: cy + R * 1.25,
    ports: {
      E: { p: v3(w / 2, cy, 0), n: v3(1, 0, 0) },
      W: { p: v3(-w / 2, cy, 0), n: v3(-1, 0, 0) },
      N: { p: v3(0, cy + R * 1.25, 0), n: v3(0, 1, 0) },
      S: { p: v3(-bodyLen * 0.12, cy - R, 0), n: v3(0, -1, 0) },
    },
    footprint: rectFoot(w, R * 2.2),
  };
};

/** Vertical tank — dished top, skirt base, level gauge. */
export const vtank: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(w, d) / 2;
  const skirtH = h * 0.1;
  const roofH = R * 0.55;
  return {
    parts: [
      p(translate(cylinder(R * 0.92, skirtH, { seg: 22, capTop: false }), 0, 0, 0), 'galv'),
      p(revolve(vesselProfile(R, skirtH, h, R * 0.35, R * 0.2, 'dish', roofH), 24, false), 'stainless'),
      pm(
        'stainless',
        translate(flange(R * 0.3, 2, 12), 0, h, 0),
        nozzle(v3(R * 0.98, h * 0.62, 0), v3(1, 0, 0), R * 0.15, 4),
        translate(cylinder(R * 0.2, skirtH * 0.7, { seg: 10 }), 0, skirtH * 0.2, 0),
      ),
      // Sight-glass level gauge with its two isolation nozzles.
      pm('glass', translate(cylinder(1.3, h * 0.45, { seg: 8 }), R * 1.12, h * 0.28, 0)),
      pm(
        'brass',
        translate(cylinder(1.9, 2, { seg: 8 }), R * 1.12, h * 0.28, 0),
        translate(cylinder(1.9, 2, { seg: 8 }), R * 1.12, h * 0.73, 0),
      ),
    ],
    height: h,
    ports: {
      N: { p: v3(0, h, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, skirtH * 0.2, 0), n: v3(0, -1, 0) },
      E: { p: v3(R * 1.05, h * 0.62, 0), n: v3(1, 0, 0) },
      W: { p: v3(-R, h * 0.62, 0), n: v3(-1, 0, 0) },
    },
    footprint: roundFoot(R * 1.08, 12),
  };
};

/** Bulk bag (FIBC) hanging in its discharge frame. */
export const bulkbag: Builder = ({ w, h, d }): Model3 => {
  const frameH = h;
  const bagW = w * 0.6;
  const bagH = h * 0.5;
  const bagY = h * 0.32;
  const post = (x: number, z: number) => translate(boxCentered(2.4, frameH, 2.4), x, frameH / 2, z);
  return {
    parts: [
      pm(
        'galv',
        post(w / 2 - 2, d / 2 - 2),
        post(-(w / 2 - 2), d / 2 - 2),
        post(w / 2 - 2, -(d / 2 - 2)),
        post(-(w / 2 - 2), -(d / 2 - 2)),
        translate(boxCentered(w - 2, 2.4, 2.4), 0, frameH, d / 2 - 2),
        translate(boxCentered(w - 2, 2.4, 2.4), 0, frameH, -(d / 2 - 2)),
        translate(boxCentered(2.4, 2.4, d - 2), w / 2 - 2, frameH, 0),
        translate(boxCentered(2.4, 2.4, d - 2), -(w / 2 - 2), frameH, 0),
      ),
      // The bag itself: a slightly bellied sack tapering to its outlet spout.
      p(
        merge(
          translate(frustum(bagW * 0.52, bagW * 0.5, bagH, { seg: 12 }), 0, bagY, 0),
          translate(frustum(bagW * 0.12, bagW * 0.52, bagH * 0.3, { seg: 12, capBottom: false }), 0, bagY - bagH * 0.3, 0),
        ),
        'plastic',
      ),
      p(translate(cylinder(bagW * 0.1, bagY - bagH * 0.3, { seg: 10 }), 0, 0, 0), 'plastic'),
      p(translate(cylinder(bagW * 0.14, 3, { seg: 10 }), 0, 2, 0), 'stainless'),
    ],
    height: frameH,
    ports: {
      N: { p: v3(0, frameH, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, 2, 0), n: v3(0, -1, 0) },
      E: { p: v3(w / 2, h * 0.45, 0), n: v3(1, 0, 0) },
      W: { p: v3(-w / 2, h * 0.45, 0), n: v3(-1, 0, 0) },
    },
    footprint: rectFoot(w, d),
  };
};

/** IBC — the moulded tote in its galvanised cage on a pallet. */
export const ibc: Builder = ({ w, h, d }): Model3 => {
  const palletH = h * 0.12;
  const bodyH = h - palletH;
  const bars: Mesh[] = [];
  for (let i = 1; i < 5; i++)
    bars.push(translate(boxCentered(w * 0.98, 1.1, d * 0.98), 0, palletH + (bodyH * i) / 5, 0));
  for (let i = 0; i <= 4; i++) {
    const x = -w / 2 + (w * i) / 4;
    bars.push(translate(boxCentered(1.1, bodyH, 1.1), x, palletH + bodyH / 2, d / 2));
    bars.push(translate(boxCentered(1.1, bodyH, 1.1), x, palletH + bodyH / 2, -d / 2));
  }
  return {
    parts: [
      p(merge(box(w, palletH * 0.6, d), translate(box(w * 0.9, palletH * 0.4, d * 0.2), 0, palletH * 0.6, 0)), 'concrete'),
      p(translate(box(w * 0.94, bodyH * 0.98, d * 0.94), 0, palletH, 0), 'plastic'),
      p(merge(...bars), 'galv'),
      pm(
        'stainless',
        translate(cylinder(w * 0.11, 3, { seg: 10 }), 0, h, 0),
        translate(cylinderZ(w * 0.07, 5, 10), 0, palletH + 3, d / 2),
      ),
    ],
    height: h + 3,
    ports: {
      N: { p: v3(0, h + 3, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, palletH + 3, 0), n: v3(0, -1, 0) },
      E: { p: v3(w / 2, h * 0.5, 0), n: v3(1, 0, 0) },
      W: { p: v3(-w / 2, h * 0.5, 0), n: v3(-1, 0, 0) },
    },
    footprint: rectFoot(w, d),
  };
};

/** Super (hot) mixer — jacketed bowl, top lid, big belt-driven motor beneath. */
export const supermixer: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(w, d) / 2;
  const baseH = h * 0.34;
  const bowlH = h * 0.42;
  const lidH = h * 0.14;
  return {
    parts: [
      p(skid(w * 0.96, d * 0.96, 4), 'galv'),
      // Drive pedestal + motor slung underneath, as on a real hot mixer.
      p(translate(frustum(R * 0.62, R * 0.86, baseH, { seg: 18 }), 0, 4, 0), 'machine'),
      p(translate(motorUnit(w * 0.44, R * 0.24).body, -w * 0.06, baseH * 0.42, R * 0.7), 'motor'),
      p(translate(motorUnit(w * 0.44, R * 0.24).fins, -w * 0.06, baseH * 0.42, R * 0.7), 'motor'),
      // The jacketed bowl.
      p(
        merge(
          translate(frustum(R * 0.86, R, bowlH, { seg: 24, capTop: false }), 0, 4 + baseH, 0),
          translate(cylinder(R * 1.02, bowlH * 0.5, { seg: 24, capTop: false, capBottom: false }), 0, 4 + baseH + bowlH * 0.25, 0),
        ),
        'stainless',
      ),
      // Lid with charge port and vent.
      pm(
        'stainless',
        translate(frustum(R, R * 0.5, lidH, { seg: 24, capBottom: false }), 0, 4 + baseH + bowlH, 0),
        translate(cylinder(R * 0.22, 5, { seg: 12 }), R * 0.3, 4 + baseH + bowlH + lidH * 0.7, 0),
        translate(flange(R * 0.3, 1.6, 12), R * 0.3, 4 + baseH + bowlH + lidH * 0.7 + 5, 0),
      ),
      // Discharge chute out of the bowl wall.
      p(nozzle(v3(R * 0.98, 4 + baseH + bowlH * 0.25, 0), v3(1, 0, 0), R * 0.2, 6), 'stainless'),
      p(translate(box(w * 0.2, h * 0.3, 2.4), -w * 0.34, 4 + baseH, d * 0.42), 'panel'),
    ],
    height: 4 + baseH + bowlH + lidH + 7,
    ports: {
      N: { p: v3(R * 0.3, 4 + baseH + bowlH + lidH + 6, 0), n: v3(0, 1, 0) },
      E: { p: v3(R * 1.04 + 6, 4 + baseH + bowlH * 0.25, 0), n: v3(1, 0, 0) },
      W: { p: v3(-R, 4 + baseH + bowlH * 0.5, 0), n: v3(-1, 0, 0) },
      S: { p: v3(0, 4, 0), n: v3(0, -1, 0) },
    },
    footprint: roundFoot(R * 1.05, 12),
  };
};

/** Cold mixer — a horizontal ribbon blender with an end drive. */
export const coldmixer: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(h, d) / 2;
  const legH = h * 0.24;
  const bodyLen = w * 0.68;
  const cy = legH + R;
  // A U-shaped trough: half a cylinder with a flat lid.
  const trough = rotateZ(
    merge(cylinder(R, bodyLen, { seg: 22, capTop: true, capBottom: true })),
    -Math.PI / 2,
  );
  return {
    parts: [
      pm(
        'galv',
        translate(boxCentered(4, legH, d * 0.8), bodyLen * 0.4, legH / 2, 0),
        translate(boxCentered(4, legH, d * 0.8), -bodyLen * 0.4, legH / 2, 0),
      ),
      p(translate(trough, -bodyLen / 2, cy, 0), 'stainless'),
      // Ribbon agitator visible through the open top.
      p(translate(boxCentered(bodyLen * 0.96, 2.6, 2.6), 0, cy, 0), 'steel'),
      p(gearbox(w * 0.12, R * 1.5, d * 0.5), 'cast'),
      p(translate(motorUnit(w * 0.2, R * 0.34).body, w * 0.42, cy, 0), 'motor'),
      p(translate(motorUnit(w * 0.2, R * 0.34).fins, w * 0.42, cy, 0), 'motor'),
      p(translate(gearbox(w * 0.1, R * 1.6, d * 0.5), -w * 0.44, legH, 0), 'cast'),
      // Charge hatches on the lid.
      pm(
        'stainless',
        translate(cylinder(R * 0.3, 3, { seg: 12 }), -bodyLen * 0.22, cy + R, 0),
        translate(cylinder(R * 0.3, 3, { seg: 12 }), bodyLen * 0.22, cy + R, 0),
        translate(cylinder(R * 0.24, legH * 0.6, { seg: 10 }), 0, legH * 0.3, 0),
      ),
    ],
    height: cy + R + 3,
    ports: {
      N: { p: v3(-bodyLen * 0.22, cy + R + 3, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, legH * 0.3, 0), n: v3(0, -1, 0) },
      E: { p: v3(w / 2, cy, 0), n: v3(1, 0, 0) },
      W: { p: v3(-w / 2, cy, 0), n: v3(-1, 0, 0) },
    },
    footprint: rectFoot(w, d),
  };
};

/** Buffer tank — a slim vertical vessel on three legs. */
export const buffertank: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(w, d) / 2;
  const legH = h * 0.16;
  return {
    parts: [
      p(ringLegs(R * 0.84, legH, 3, 1.7), 'galv'),
      p(revolve(vesselProfile(R, legH, h, R * 0.5, R * 0.16, 'dish', R * 0.5), 22, false), 'stainless'),
      pm(
        'stainless',
        translate(flange(R * 0.28, 1.8, 12), 0, h, 0),
        nozzle(v3(R * 0.98, h * 0.6, 0), v3(1, 0, 0), R * 0.14, 4),
        translate(cylinder(R * 0.18, legH * 0.6, { seg: 10 }), 0, legH * 0.25, 0),
      ),
      p(translate(cylinder(1.2, h * 0.4, { seg: 8 }), R * 1.1, h * 0.3, 0), 'glass'),
    ],
    height: h,
    ports: {
      N: { p: v3(0, h, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, legH * 0.25, 0), n: v3(0, -1, 0) },
      E: { p: v3(R * 1.02, h * 0.6, 0), n: v3(1, 0, 0) },
      W: { p: v3(-R, h * 0.6, 0), n: v3(-1, 0, 0) },
    },
    footprint: roundFoot(R * 1.05, 12),
  };
};

/** Nitrogen / gas supply — a bottle bank in a rack. */
export const nitrogen: Builder = ({ w, h, d }): Model3 => {
  const bottleR = Math.min(w / 4.4, d / 2.6);
  const bottleH = h * 0.86;
  const bottles: Mesh[] = [];
  const caps: Mesh[] = [];
  for (let i = 0; i < 3; i++) {
    const x = -w / 2 + (w * (i + 0.5)) / 3;
    bottles.push(translate(cylinder(bottleR, bottleH * 0.86, { seg: 14 }), x, 0, 0));
    caps.push(translate(dome(bottleR, 12), x, bottleH * 0.86, 0));
    caps.push(translate(cylinder(bottleR * 0.3, bottleH * 0.1, { seg: 10 }), x, bottleH * 0.86 + bottleR * 0.7, 0));
  }
  return {
    parts: [
      p(merge(...bottles), 'painted'),
      p(merge(...caps), 'machine'),
      pm(
        'galv',
        translate(boxCentered(w, 2, 2), 0, bottleH * 0.72, d / 2 - 1.5),
        translate(boxCentered(w, 2, 2), 0, bottleH * 0.3, d / 2 - 1.5),
        translate(boxCentered(2, bottleH * 0.8, 2), w / 2, bottleH * 0.4, d / 2 - 1.5),
        translate(boxCentered(2, bottleH * 0.8, 2), -w / 2, bottleH * 0.4, d / 2 - 1.5),
      ),
      p(translate(cylinderX(bottleR * 0.22, w * 0.9, 10), 0, h * 0.98, 0), 'pipe'),
    ],
    height: h,
    ports: {
      E: { p: v3(w / 2, h * 0.98, 0), n: v3(1, 0, 0) },
      W: { p: v3(-w / 2, h * 0.98, 0), n: v3(-1, 0, 0) },
      N: { p: v3(0, h, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, 0, d / 2), n: v3(0, 0, 1) },
    },
    footprint: rectFoot(w, d),
  };
};

/** Bubble column reactor — a very tall slim column with a sparger and jacket. */
export const bubblecolumn: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(w, d) / 2;
  const skirtH = h * 0.07;
  const rings: Mesh[] = [];
  for (let i = 1; i < 6; i++)
    rings.push(translate(frustum(R * 1.06, R * 1.06, 2.4, { seg: 20 }), 0, skirtH + ((h - skirtH) * i) / 6, 0));
  return {
    parts: [
      p(translate(cylinder(R * 0.95, skirtH, { seg: 20, capTop: false }), 0, 0, 0), 'galv'),
      p(revolve(vesselProfile(R, skirtH, h, R * 0.4, R * 0.18, 'dish', R * 0.6), 22, false), 'stainless'),
      p(merge(...rings), 'lagging'),
      p(translate(ladder(h * 0.9), R + 3.5, 0, 0), 'galv'),
      pm(
        'stainless',
        translate(flange(R * 0.3, 1.8, 12), 0, h, 0),
        nozzle(v3(0, skirtH + 3, R * 0.9), v3(0, 0, 1), R * 0.13, 4),
        nozzle(v3(R * 0.95, h * 0.8, 0), v3(1, 0, 0), R * 0.13, 4),
        translate(cylinder(R * 0.2, skirtH * 0.6, { seg: 10 }), 0, skirtH * 0.2, 0),
      ),
    ],
    height: h,
    ports: {
      N: { p: v3(0, h, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, skirtH * 0.2, 0), n: v3(0, -1, 0) },
      E: { p: v3(R * 1.02, h * 0.8, 0), n: v3(1, 0, 0) },
      W: { p: v3(-R, h * 0.35, 0), n: v3(-1, 0, 0) },
    },
    footprint: roundFoot(R * 1.1, 12),
  };
};

/** Washing container / dry tank — an open-topped rectangular process tank. */
export const washcontainer: Builder = ({ w, h, d }): Model3 => {
  const legH = h * 0.2;
  const bodyH = h - legH;
  const t = 1.6;
  return {
    parts: [
      pm(
        'galv',
        translate(boxCentered(3, legH, 3), w / 2 - 4, legH / 2, d / 2 - 4),
        translate(boxCentered(3, legH, 3), -(w / 2 - 4), legH / 2, d / 2 - 4),
        translate(boxCentered(3, legH, 3), w / 2 - 4, legH / 2, -(d / 2 - 4)),
        translate(boxCentered(3, legH, 3), -(w / 2 - 4), legH / 2, -(d / 2 - 4)),
      ),
      // Four walls + a floor, so the tank is genuinely open at the top.
      pm(
        'stainless',
        translate(box(w, bodyH, t), 0, legH, -(d / 2 - t / 2)),
        translate(box(w, bodyH, t), 0, legH, d / 2 - t / 2),
        translate(box(t, bodyH, d), w / 2 - t / 2, legH, 0),
        translate(box(t, bodyH, d), -(w / 2 - t / 2), legH, 0),
        translate(box(w, t, d), 0, legH, 0),
      ),
      p(translate(box(w - t * 2, bodyH * 0.68, d - t * 2), 0, legH + t, 0), 'water'),
      p(nozzle(v3(-w / 2, legH + bodyH * 0.2, 0), v3(-1, 0, 0), 2.4, 4), 'stainless'),
    ],
    height: h,
    ports: {
      N: { p: v3(0, h, 0), n: v3(0, 1, 0) },
      E: { p: v3(w / 2, legH + bodyH * 0.6, 0), n: v3(1, 0, 0) },
      W: { p: v3(-w / 2 - 4, legH + bodyH * 0.2, 0), n: v3(-1, 0, 0) },
      S: { p: v3(0, legH, d / 2), n: v3(0, 0, 1) },
    },
    footprint: rectFoot(w, d),
  };
};


/**
 * Octabin — the octagonal fibreboard bulk box that pellets and granulate are
 * delivered in.
 *
 * It is genuinely an OCTAGON, not a chamfered square: eight equal flats, the
 * two facing each axis square-on, sitting on a timber pallet under a shallow
 * capping lid with strapping round the body. The 2D symbol's width is taken as
 * the flat-to-flat dimension, which is how an octabin is actually specified,
 * so the circumradius is scaled up by 1/cos(22.5°) to suit.
 */
export const octabin: Builder = ({ w, h, d }): Model3 => {
  const across = Math.min(w, d);
  // Flat-to-flat → circumradius, with the flats square to the axes.
  const R = across / 2 / Math.cos(Math.PI / 8);
  const oct = (r: number): Vec2[] => ngon(r, 8, Math.PI / 8);

  const palletH = h * 0.1;
  const lidH = h * 0.055;
  const bodyH = h - palletH - lidH;
  const bodyY = palletH;

  // Timber pallet: three bearers under a slatted top deck.
  const bearers: Mesh[] = [];
  for (const t of [-1, 0, 1])
    bearers.push(translate(box(across * 1.02, palletH * 0.55, across * 0.14), 0, 0, t * across * 0.4));
  const deck: Mesh[] = [];
  for (let i = 0; i < 5; i++)
    deck.push(
      translate(
        box(across * 1.02, palletH * 0.45, across * 0.15),
        0,
        palletH * 0.55,
        -across * 0.42 + (across * 0.84 * i) / 4,
      ),
    );

  // Strapping: two bands round the body.
  const bands: Mesh[] = [];
  for (const f of [0.3, 0.72])
    bands.push(
      translate(
        extrude(oct(R * 1.015), h * 0.022),
        0,
        bodyY + bodyH * f,
        0,
      ),
    );

  return {
    parts: [
      pm('timber', merge(...bearers), merge(...deck)),
      p(translate(extrude(oct(R), bodyH), 0, bodyY, 0), 'cardboard'),
      // Capping lid, a touch proud of the body so the joint reads.
      p(translate(extrude(oct(R * 1.03), lidH), 0, bodyY + bodyH, 0), 'cardboard'),
      p(merge(...bands), 'plastic'),
      // Discharge spout in the base, and a top fill collar.
      pm(
        'stainless',
        translate(cylinder(R * 0.16, palletH * 0.5, { seg: 10 }), 0, palletH * 0.25, 0),
        translate(cylinder(R * 0.2, lidH * 0.8, { seg: 12 }), 0, bodyY + bodyH + lidH, 0),
      ),
    ],
    height: bodyY + bodyH + lidH + lidH * 0.8,
    ports: {
      N: { p: v3(0, bodyY + bodyH + lidH + lidH * 0.8, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, palletH * 0.25, 0), n: v3(0, -1, 0) },
      E: { p: v3(across / 2, bodyY + bodyH * 0.6, 0), n: v3(1, 0, 0) },
      W: { p: v3(-across / 2, bodyY + bodyH * 0.6, 0), n: v3(-1, 0, 0) },
    },
    footprint: oct(R * 1.03),
  };
};
